<script lang="ts">
    import type {FormControl} from '../../forms/form-control';
    import {FormControlBuilder} from '../../forms/form-control';
    import {formControls} from '../../forms/use.form-controls';
    import type {ValueEditorSize} from '../../../types';
    import {style} from 'svelte-forms';
    import {onDestroy} from 'svelte';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import type {FormControlOptions} from '../../forms/types';
    import type {TextValueEditorValidations} from '../text/types';
    import {getContext} from 'svelte';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import {emptyAsNullFormatter, emptyAsNullParser} from '../_shared/editors-utils';
    import type {PasswordValueEditorLocalizations, PasswordValueEditorOptions} from './types';
    import Description from '../../description/Description.svelte';
    import {createInputDescription} from '../../description/utils';
    import {validatorFactory} from '../text/utils';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import {required} from '../../forms/use.required';
    import {getLogger} from 'core/svelte-context/context';

    export let model: string;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: PasswordValueEditorOptions;
    export let validations: TextValueEditorValidations;

    let passwordRepetition = model;

    const logger = getLogger();

    const localize: EditorsLocalizationFunction<PasswordValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations, 'text'))
        .withOptions(formControlOptions)
        .withModifiers([emptyAsNullParser(options)])
        .withFormatters([emptyAsNullFormatter(options)])
        .build(logger);

    const formControlRepetition = FormControlBuilder.for(`${editorId}-repetition`, `${editorName}-repetition`, passwordRepetition)
        .withValidators(validatorFactory(validations, 'text'))
        .withOptions(formControlOptions)
        .withModifiers([emptyAsNullParser(options)])
        .withFormatters([emptyAsNullFormatter(options)])
        .build(logger);

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const subscriptionRepetition = formControlRepetition.getModelValue$().subscribe((value) => {
        passwordRepetition = value;
        if (options.withConfirmation) {
            formControl.validate();
        }
    });  // two-way data binding -> sending updated value up to a parent
    $: formControlRepetition.setModelValue(passwordRepetition); // two-way data binding -> receiving new model value from parent

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    if (options.withConfirmation) {
        formControl.addValidator((value) => {
            return ({valid: !(value || passwordRepetition) || passwordRepetition === value, name: 'passwords-equals'})
        })
    }

    onDestroy(() => {
        subscription.unsubscribe();
        subscriptionRepetition.unsubscribe();
    })

    export function getFormController(): FormControl<string> {
        return formControl;
    }
</script>

<template>
    {#if options.withConfirmation}
        <div class="with-confirmation">
            <div class="form-group form-group-{size}">
                <input class="form-control input-{size}"
                       type="password"
                       id={editorId}
                       name={editorName}
                       placeholder={placeholder}
                       disabled={isDisabled}
                       maxlength={validations.maxlength}
                       minlength={validations.minlength}
                       use:required={validations.required}
                       autocomplete="new-password"
                       use:ariaDescribedby={inputDescription}
                       use:ariaErrormessage={formControl}
                       use:ariaInvalid={formControl}
                       use:focusOnMount={isFocused}
                       use:formControls={formControl}
                       use:style={{field: formControl.getFieldStateAsStore()}}
                       use:touched={{formControl}}
                       data-main-input/>
                <span class="help-block size-{size}">
                    {#if !validations.required}
                         <span class="text-muted size-{size}">
                            {localize('noChangeIfEmpty')}
                        </span>
                    {/if}
                </span>
            </div>
            <div class="form-group form-group-{size}">
                <label for="{editorId ?? 'password'}-repetition">
                    {localize('confirmPassword')}
                </label>
                <input class="form-control confirmation input-{size}"
                       type="password"
                       id="{editorId ?? 'password'}-repetition"
                       name="{editorName}-repetition"
                       placeholder={placeholder}
                       disabled={isDisabled}
                       maxlength={validations.maxlength}
                       minlength={validations.minlength}
                       use:required={validations.required}
                       autocomplete="new-password"
                       use:ariaInvalid={formControl}
                       use:formControls={formControlRepetition}
                       use:style={{field: formControl.getFieldStateAsStore()}}
                       use:touched={{formControl}}/>
            </div>
        </div>
    {:else}
        <span class="without-confirmation">
                <input class="form-control input-{size}"
                       type="password"
                       id="{editorId}"
                       name="{editorName}"
                       placeholder={placeholder}
                       disabled={isDisabled}
                       maxlength={validations.maxlength}
                       minlength={validations.minlength}
                       autocomplete="new-password"
                       use:ariaDescribedby={inputDescription}
                       use:ariaErrormessage={formControl}
                       use:ariaInvalid={formControl}
                       use:focusOnMount={isFocused}
                       use:formControls={formControl}
                       use:style={{field: formControl.getFieldStateAsStore()}}
                       use:touched={{formControl}}
                       data-main-input/>
        </span>
    {/if}
    <ErrorMessages formController={formControl} {forceShowErrors} {size}/>
    <Description {inputDescription}/>
</template>

<style global lang="less">
    .with-confirmation {
        > .form-group {
            margin: 0;
        }

        .help-block {
            margin-bottom: 0;
        }
    }

    .form-group.form-group-sm label {
        font-size: 12px;
    }

    .form-group.form-group-xs label {
        font-size: 12px;
        margin-bottom: 0;
    }

    .text-muted.size-sm,
    .text-muted.size-xs {
        font-size: 12px;
    }

    .help-block.size-xs {
        margin-top: 0;
    }
</style>